import { useState, useEffect, useCallback, useRef } from "react";
import { SponsorAdCard } from "./sponsor-ad-card";
import { track } from "@/lib/telemetry";
import type { SponsorAdPublic } from "@dugads/shared";

const BASE_INTERVAL_MS = 5000;

interface SponsorAdCarouselProps {
  ads: SponsorAdPublic[];
  schoolId?: string;
}

/**
 * Carousel for multiple sponsor ads. Rotates automatically with smooth crossfade.
 * Higher-priority ads get proportionally longer display time.
 * Tracks impressions via telemetry.
 */
export function SponsorAdCarousel({ ads, schoolId }: SponsorAdCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Compute weighted durations: priority acts as a multiplier (min 1)
  const getDuration = useCallback(
    (index: number) => {
      const weight = Math.max(ads[index]?.priority ?? 0, 1);
      return BASE_INTERVAL_MS * weight;
    },
    [ads],
  );

  // Track impression for current ad
  const trackImpression = useCallback(
    (index: number) => {
      const ad = ads[index];
      if (!ad) return;
      // Track every rotation, not just first -- for fair billing metrics
      track({
        type: "action",
        page: "event_detail",
        action: "sponsor_ad_impression",
        value: ad.config_id,
      });
    },
    [ads],
  );

  // Advance to next ad with crossfade
  const advance = useCallback(() => {
    setFading(true);
    setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % ads.length);
      setFading(false);
    }, 300); // Match CSS transition duration
  }, [ads.length]);

  // Auto-rotation
  useEffect(() => {
    if (ads.length <= 1) return;

    const scheduleNext = () => {
      clearTimeout(intervalRef.current);
      intervalRef.current = setTimeout(() => {
        advance();
      }, getDuration(activeIndex));
    };

    scheduleNext();
    return () => clearTimeout(intervalRef.current);
  }, [ads.length, activeIndex, advance, getDuration]);

  // Track impression on each rotation
  useEffect(() => {
    trackImpression(activeIndex);
  }, [activeIndex, trackImpression]);

  if (ads.length === 0) return null;
  if (ads.length === 1) return <SponsorAdCard ad={ads[0]} schoolId={schoolId} />;

  return (
    <div className="relative">
      <div
        className="transition-opacity duration-300 ease-in-out"
        style={{ opacity: fading ? 0 : 1 }}
      >
        <SponsorAdCard ad={ads[activeIndex]} schoolId={schoolId} />
      </div>

      {/* Dot indicators */}
      <div className="flex justify-center gap-1.5 mt-2">
        {ads.map((ad, i) => (
          <button
            key={ad.config_id}
            onClick={() => {
              if (i === activeIndex) return;
              setFading(true);
              setTimeout(() => {
                setActiveIndex(i);
                setFading(false);
              }, 300);
            }}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === activeIndex ? "w-4 bg-primary" : "w-1.5 bg-muted-foreground/30"
            }`}
            aria-label={`Show ad from ${ad.provider_name}`}
          />
        ))}
      </div>
    </div>
  );
}
